import React, { useEffect, useRef, useState } from 'react';

import LoadingState from "../ui/loading-state";
import { cn } from "@/lib/utils";
import { pyodideService } from '@/services/PyodideService';

interface WebPreviewProps {
    scriptPath?: string;
}

interface ComponentUpdateData {
    components: Record<string, unknown>;
}

const WebPreview: React.FC<WebPreviewProps> = ({ scriptPath }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const isInitialized = useRef(false);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    const messageListenersRef = useRef<Function[]>([]);

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;
        const initialize = async () => {
            try {
                // Load the iframe first and wait for it to signal ready
                setIsLoading(false); // This allows the iframe to render

                // Wait for the iframe to load and signal it's ready
                await new Promise<void>((resolve) => {
                    const handleIframeReady = (event: MessageEvent) => {
                        if (event.data && event.data.type === 'iframe_ready') {
                            window.removeEventListener('message', handleIframeReady);
                            console.log('Iframe signaled ready');
                            resolve();
                        }
                    };
                    window.addEventListener('message', handleIframeReady);

                    // Set a timeout in case the iframe never signals ready
                    setTimeout(() => {
                        window.removeEventListener('message', handleIframeReady);
                        console.log('Iframe ready timeout - proceeding anyway');
                        resolve();
                    }, 5000);
                });

                // Initialize Pyodide through the service if not already initialized
                if (!pyodideService.isReady()) {
                    console.log('Initializing Pyodide through worker...');
                    const initialized = await pyodideService.initialize();
                    if (!initialized) {
                        throw new Error('Failed to initialize Pyodide');
                    }
                    console.log('Pyodide worker initialized successfully');
                }

                // Run the script
                const scriptToRun = scriptPath || `/project/hello.py`;
                console.log(`Running script: ${scriptToRun}`);

                const result = await pyodideService.startScript(scriptToRun);
                if (!result.success) {
                    throw new Error(`Failed to run script: ${result.error}`);
                }

                await fetchAndPassBranding();

                console.log('Script executed successfully');

                // Let iframe know we're connected
                if (iframeRef.current && iframeRef.current.contentWindow) {
                    iframeRef.current.contentWindow.postMessage({
                        type: 'connection_status',
                        connected: true
                    }, '*');
                }

                // Send components to iframe
                if (result.components) {
                    if (iframeRef.current && iframeRef.current.contentWindow) {
                        iframeRef.current.contentWindow.postMessage({
                            type: 'components',
                            components: result.components
                        }, '*');
                    }
                }

                // Set up message relays
                setupMessageRelays();
            } catch (err) {
                console.error('Initialization error:', err);
                setError(err instanceof Error ? err.message : String(err));
                setIsLoading(false);
            }
        };

        const setupMessageRelays = () => {
            // Handle messages from iframe and relay to Pyodide worker
            const handleIframeMessage = (event: MessageEvent) => {
                // Make sure message is from our iframe
                if (event.source !== iframeRef.current?.contentWindow) return;

                // Parse message if it's a string
                let message;
                try {
                    message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                } catch (e) {
                    console.error('Error parsing iframe message:', e);
                    return;
                }

                // Relay component updates to Pyodide
                if (message.type === 'component_update') {
                    // Extract the component ID and value
                    const componentId = message.id || message.component_id;
                    const value = message.value;

                    if (componentId && value !== undefined) {
                        pyodideService.updateComponent(componentId, value)
                            .catch(err => {
                                console.error('[Parent] Error updating component:', err);
                            });
                    }
                }
            };

            // Add the event listener
            window.addEventListener('message', handleIframeMessage);
            messageListenersRef.current.push(() => window.removeEventListener('message', handleIframeMessage));

            // Listen for worker messages and forward to iframe
            // This is handled by the PyodideService callback system
            const handleComponentUpdate = (data: ComponentUpdateData) => {
                if (iframeRef.current && iframeRef.current.contentWindow) {
                    iframeRef.current.contentWindow.postMessage({
                        type: 'components',
                        components: data.components
                    }, '*');
                }
            };

            // Subscribe to component updates from the worker
            const unsubscribeComponentUpdate = pyodideService.subscribeToWorkerMessage(
                'COMPONENT_UPDATE',
                handleComponentUpdate
            );

            // Add this unsubscribe function to our cleanup array
            messageListenersRef.current.push(unsubscribeComponentUpdate);
        };

        const fetchAndPassBranding = async () => {
            try {
                // Get branding info from PyodideService
                const branding = await pyodideService.getBrandingInfo();

                console.log("Branding from pyodideService: ", branding);

                // Pass to iframe
                if (iframeRef.current?.contentWindow && branding) {
                    iframeRef.current.contentWindow.postMessage({
                        type: 'branding',
                        branding: branding
                    }, '*');
                }
            } catch (error) {
                console.error("Error fetching branding:", error);
            }
        };

        initialize();

        return () => {
            console.log('Cleaning up WebPreview component...');
            // Clean up all message listeners
            messageListenersRef.current.forEach(removeListener => removeListener());
            // Stop the running script
            pyodideService.stopScript().catch(console.error);
        };
    }, []); // Empty dependency array for initialization

    // New effect to handle script path changes
    useEffect(() => {
        if (!isInitialized.current) return;

        const runScript = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Stop any running script
                await pyodideService.stopScript();

                // Run the new script
                const scriptToRun = scriptPath || `/project/hello.py`;
                console.log(`Running script: ${scriptToRun}`);

                const result = await pyodideService.startScript(scriptToRun);
                if (!result.success) {
                    throw new Error(`Failed to run script: ${result.error}`);
                }

                // Send components to iframe
                if (result.components && iframeRef.current?.contentWindow) {
                    iframeRef.current.contentWindow.postMessage({
                        type: 'components',
                        components: result.components
                    }, '*');
                }

                setIsLoading(false);
            } catch (err) {
                console.error('Error running script:', err);
                setError(err instanceof Error ? err.message : String(err));
                setIsLoading(false);
            }
        };

        runScript();
    }, [scriptPath]); // Run when scriptPath changes

    // Add effect to handle file change events
    useEffect(() => {
        if (!isInitialized.current) return;

        const handleFileChanged = async (event: Event) => {
            const customEvent = event as CustomEvent<{ path: string }>;
            console.log('File changed, refreshing preview:', customEvent.detail.path);
            try {
                setIsRefreshing(true);
                setError(null);

                // Stop any running script
                await pyodideService.stopScript();

                // Run the script again
                const result = await pyodideService.startScript(scriptPath || '/project/hello.py');
                if (!result.success) {
                    throw new Error(`Failed to run script: ${result.error}`);
                }

                // Send components to iframe
                if (result.components && iframeRef.current?.contentWindow) {
                    iframeRef.current.contentWindow.postMessage({
                        type: 'components',
                        components: result.components
                    }, '*');
                }

                // Add a small delay to ensure smooth transition
                await new Promise(resolve => setTimeout(resolve, 100));
                setIsRefreshing(false);
            } catch (err) {
                console.error('Error refreshing preview:', err);
                setError(err instanceof Error ? err.message : String(err));
                setIsRefreshing(false);
            }
        };

        // Add event listener
        window.addEventListener('filesystem:file-changed', handleFileChanged);

        return () => {
            // Remove event listener on cleanup
            window.removeEventListener('filesystem:file-changed', handleFileChanged);
        };
    }, [scriptPath]); // Re-run when scriptPath changes

    const iframeContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Preswald Preview</title>
            <link rel="stylesheet" href="/preswald-assets/preswald-app.css">
            <script>
            // Listen for branding message and attach to window
            window.addEventListener('message', function(event) {
                if (event.data && event.data.type === 'branding') {
                    // Attach branding to window object
                    window.PRESWALD_BRANDING = event.data.branding;
                    
                    // Dispatch an event to notify app that branding is available
                    window.dispatchEvent(new CustomEvent('branding-loaded'));
                }
            });
            </script>
        </head>
        <body>
            <div id="root"></div>
            <script type="module" src="/preswald-assets/preswald-app.js"></script>
            <script>
                // Send ready signal to parent as soon as possible
                window.addEventListener('DOMContentLoaded', () => {
                    console.log('Iframe DOM loaded, signaling ready to parent');
                    window.parent.postMessage({type: 'iframe_ready'}, '*');
                });

                // Also send another ready signal after full load for reliability
                window.addEventListener('load', () => {
                    console.log('Iframe fully loaded');
                    window.parent.postMessage({type: 'iframe_ready'}, '*');
                });
            </script>
        </body>
        </html>
    `;

    return (
        <div className="relative w-full h-full transition-all duration-300 ease-in-out transform">
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50 transition-all duration-300 ease-in-out transform translate-x-0">
                    <LoadingState />
                </div>
            )}
            {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50 transition-all duration-300 ease-in-out transform translate-x-0">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md">
                        {error}
                    </div>
                </div>
            )}
            <div className={cn(
                "w-full h-full transition-all duration-300 ease-in-out",
                isRefreshing && "opacity-50"
            )}>
                <iframe
                    ref={iframeRef}
                    className="w-full h-full border-0 transition-all duration-300 ease-in-out transform translate-x-0"
                    title="Preswald Preview"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                    srcDoc={iframeContent}
                />
            </div>
        </div>
    );
};

export default WebPreview;